import { ensureI18nInitialized, useI18n } from './stores/i18n';
import {
  cleanLang,
  getLangFromBrowser,
  getLangFromStorage,
  getLangFromUrl,
} from './utils/i18n';

async function savePreferredLanguageToBackend(lang: string): Promise<void> {
  if (typeof URL_API_HOST === 'undefined') {
    return;
  }

  try {
    const response = await fetch(
      `${URL_API_HOST}/api/v1/languages/preferred/${lang}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      console.error(
        `Error saving preferred language to ${lang}. Status: ${response.status}`,
      );
    }
  } catch (error) {
    console.error('Exception saving preferred language:', error);
  }
}

export class Translator {
  static async setLanguageCode(preferredLanguage: string): Promise<void> {
    ensureI18nInitialized();
    const currentLang = useI18n.getState().lang;
    const newLang = cleanLang(preferredLanguage);

    if (newLang !== currentLang) {
      await savePreferredLanguageToBackend(newLang);
    }

    await useI18n.getState().setLang(newLang);
  }

  static getLanguageCode(): string {
    ensureI18nInitialized();
    return useI18n.getState().lang;
  }

  static getLanguageSource(): string {
    ensureI18nInitialized();
    const currentLang = useI18n.getState().lang;
    const langFromUrl = getLangFromUrl();
    const langFromStorage = getLangFromStorage();
    const langFromBrowser = getLangFromBrowser();
    if (currentLang === langFromUrl) return 'url';
    if (currentLang === langFromStorage) return 'storage';
    if (currentLang === langFromBrowser) return 'browser';
    return 'default';
  }

  static getDateLocale() {
    ensureI18nInitialized();
    return useI18n.getState().dateLocale;
  }

  static translateText(
    translateKey: string,
    replacement: any = {},
    defaultValue = '',
  ): string {
    ensureI18nInitialized();
    const { translate } = useI18n.getState();
    return translate(translateKey, replacement, {
      textOnly: true,
      defaultValue,
    }) as string;
  }

  static translate(
    translateKey: string,
    replacement: any = {},
    textOnly?: boolean,
    defaultValue?: string | JSX.Element,
  ): any {
    ensureI18nInitialized();
    const { translate } = useI18n.getState();
    return translate(translateKey, replacement, { textOnly, defaultValue });
  }

  static i18n(key: string, defaultText?: string): string {
    ensureI18nInitialized();
    const { t } = useI18n.getState();
    return t(key, defaultText);
  }
}

export { getLangFromUrl } from './utils/i18n';
